import React from "react";
import Skeleton from "../ui/Skeleton";

interface AutoResponderSkeletonProps {
  activeTab?: "monitoramento" | "historico" | "conhecimento" | "configuracao";
  responsesView?: "list" | "kanban";
}

const TAB_LABEL_WIDTHS = [95, 65, 150, 95];

/** Cabeçalho tipo "pill" (ícone + título [+ ação/badge à direita]), h-[52px] fixo — mesmo padrão usado em todos os cards principais da página real. */
const PillHeaderSkeleton: React.FC<{
  titleWidth?: number;
  withRightContent?: "badge" | "button" | "text" | false;
}> = ({ titleWidth = 160, withRightContent = false }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap h-[52px] bg-white/70 border border-purple-200 rounded-2xl px-4 sm:px-5 py-2.5 shadow-xs">
    <div className="flex items-center gap-2">
      <Skeleton variant="circular" width={20} height={20} />
      <Skeleton
        variant="rectangular"
        width={titleWidth}
        height={18}
        className="rounded-sm"
      />
    </div>
    {withRightContent === "badge" && (
      <Skeleton
        variant="rectangular"
        width={100}
        height={18}
        className="rounded-full"
      />
    )}
    {withRightContent === "button" && (
      <Skeleton
        variant="rectangular"
        width={90}
        height={36}
        className="rounded-lg"
      />
    )}
    {withRightContent === "text" && (
      <Skeleton
        variant="rectangular"
        width={110}
        height={9}
        className="rounded-sm"
      />
    )}
  </div>
);

/** Cabeçalho interno de card (SectionHeader real: caixa de ícone 40x40 + título/subtítulo). */
const SectionHeaderSkeleton: React.FC<{ withAction?: boolean }> = ({
  withAction,
}) => (
  <div className="flex items-start gap-3 border-b pb-2 border-purple-100 mb-3">
    <Skeleton
      variant="rectangular"
      width={40}
      height={40}
      className="rounded-xl"
    />
    <div className="flex-1 min-w-0 h-10 flex flex-col justify-between">
      <Skeleton
        variant="rectangular"
        width={150}
        height={11}
        className="rounded-sm"
      />
      <Skeleton
        variant="rectangular"
        width={220}
        height={9}
        className="rounded-sm"
      />
    </div>
    {withAction && (
      <Skeleton
        variant="rectangular"
        width={80}
        height={36}
        className="rounded-lg"
      />
    )}
  </div>
);

export const AutoResponderSkeleton: React.FC<AutoResponderSkeletonProps> = ({
  activeTab = "monitoramento",
  responsesView = "kanban",
}) => {
  return (
    <div className="w-full animate-pulse">
      {/* Navbar de tabs - mesmo controle segmentado da página real (ícone + label) */}
      <div className="sticky top-0 z-30 -mt-4 -mx-2 bg-white/95 backdrop-blur-md border-b border-purple-200">
        <div className="w-full flex gap-1 bg-gray-100 p-1">
          {TAB_LABEL_WIDTHS.map((w, i) => (
            <div
              key={i}
              className="flex-1 h-8 flex items-center justify-center gap-1.5 rounded-lg bg-white border border-gray-200"
            >
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton
                variant="rectangular"
                width={w}
                height={10}
                className="rounded-sm"
              />
            </div>
          ))}
        </div>

        {/* Barra de filtros (2 linhas) — só existe na aba Histórico */}
        {activeTab === "historico" && (
          <div className="w-full px-4 py-2 flex flex-col gap-2 border-t border-purple-100">
            <div className="flex flex-row flex-wrap gap-2 items-center">
              <Skeleton
                variant="rectangular"
                className="flex-1 min-w-[200px] h-9 rounded-xl"
              />
              <Skeleton
                variant="rectangular"
                width={230}
                height={36}
                className="rounded-xl"
              />
              <Skeleton
                variant="rectangular"
                width={250}
                height={36}
                className="rounded-xl"
              />
              <Skeleton
                variant="rectangular"
                width={76}
                height={36}
                className="rounded-xl"
              />
              <Skeleton
                variant="rectangular"
                width={76}
                height={36}
                className="rounded-xl"
              />
            </div>
            <div className="flex flex-row flex-wrap gap-2 items-center">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  className="flex-1 min-w-[160px] h-9 rounded-xl"
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2 w-full mt-2">
        {activeTab === "monitoramento" && (
          <div className="space-y-2">
            <PillHeaderSkeleton titleWidth={140} withRightContent="text" />

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-purple-200 p-4 flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between">
                    <Skeleton
                      variant="rectangular"
                      width={70}
                      height={8}
                      className="rounded-sm"
                    />
                    <Skeleton
                      variant="rectangular"
                      width={36}
                      height={36}
                      className="rounded-lg"
                    />
                  </div>
                  <Skeleton
                    variant="rectangular"
                    width={55}
                    height={24}
                    className="rounded-sm"
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={24}
                    className="rounded-lg"
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Atividade Recente */}
              <div className="lg:col-span-2 flex flex-col bg-white border border-purple-200 rounded-3xl p-3 sm:p-4">
                <SectionHeaderSkeleton withAction />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-1.5 py-1 rounded-2xl border border-purple-200"
                    >
                      <Skeleton
                        variant="rectangular"
                        width={44}
                        height={44}
                        className="rounded-xl"
                      />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton
                          variant="rectangular"
                          width={110}
                          height={9}
                          className="rounded-sm"
                        />
                        <Skeleton
                          variant="rectangular"
                          width="80%"
                          height={8}
                          className="rounded-sm"
                        />
                      </div>
                      <Skeleton
                        variant="rectangular"
                        width={60}
                        height={20}
                        className="rounded-full"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Teste Rápido */}
              <div className="bg-white border border-purple-200 rounded-3xl p-3 sm:p-4">
                <SectionHeaderSkeleton />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={46}
                  className="rounded-xl mb-3"
                />
                <div className="space-y-2.5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-1.5">
                      <Skeleton
                        variant="rectangular"
                        width="40%"
                        height={10}
                        className="rounded-sm"
                      />
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                        className="rounded-xl"
                      />
                    </div>
                  ))}
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={36}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "historico" && responsesView === "kanban" && (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {[1, 2, 3, 4, 5].map((col) => (
              <div
                key={col}
                className="flex flex-col min-w-[240px] flex-1 max-w-[310px]"
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={44}
                  className="rounded-2xl mb-3"
                />
                <div className="rounded-2xl border-2 border-dashed border-purple-100 p-2 space-y-2 min-h-[160px]">
                  {[1, 2].map((c) => (
                    <Skeleton
                      key={c}
                      variant="rectangular"
                      width="100%"
                      height={130}
                      className="rounded-2xl"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "historico" && responsesView === "list" && (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="rounded-3xl border border-purple-200 bg-white p-3 space-y-2.5"
              >
                {/* Header: plataforma, status, confiança, ações */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      className="rounded-xl"
                    />
                    <div className="space-y-1.5">
                      <Skeleton
                        variant="rectangular"
                        width={90}
                        height={10}
                        className="rounded-sm"
                      />
                      <Skeleton
                        variant="rectangular"
                        width={110}
                        height={8}
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={18}
                      className="rounded-full"
                    />
                    <Skeleton
                      variant="rectangular"
                      width={80}
                      height={18}
                      className="rounded-lg"
                    />
                    <Skeleton
                      variant="rectangular"
                      width={32}
                      height={32}
                      className="rounded-lg"
                    />
                  </div>
                </div>
                {/* Gatilho + resposta */}
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={32}
                  className="rounded-xl"
                />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={48}
                  className="rounded-xl"
                />
              </div>
            ))}
          </div>
        )}

        {activeTab === "conhecimento" && (
          <div className="space-y-2">
            <PillHeaderSkeleton titleWidth={180} />

            {/* Fontes Externas (RAG) */}
            <div className="bg-white border border-purple-200 rounded-3xl p-3 sm:p-4">
              <SectionHeaderSkeleton withAction />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={52}
                className="rounded-xl"
              />
            </div>

            {/* Templates de Resposta */}
            <div className="bg-white border border-purple-200 rounded-3xl p-3 sm:p-4">
              <SectionHeaderSkeleton withAction />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="rounded-3xl border border-purple-200 overflow-hidden bg-white"
                  >
                    <div className="p-3 pb-2 space-y-2">
                      <div className="flex justify-between items-start">
                        <Skeleton
                          variant="rectangular"
                          width={32}
                          height={32}
                          className="rounded-xl"
                        />
                        <Skeleton
                          variant="rectangular"
                          width={55}
                          height={20}
                          className="rounded-full"
                        />
                      </div>
                      <Skeleton
                        variant="rectangular"
                        width="70%"
                        height={10}
                        className="rounded-sm"
                      />
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={54}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="p-3 pt-2 border-t border-purple-50 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Skeleton
                          variant="rectangular"
                          width={32}
                          height={32}
                          className="rounded-lg"
                        />
                        <Skeleton
                          variant="rectangular"
                          width={32}
                          height={32}
                          className="rounded-lg"
                        />
                      </div>
                      <Skeleton
                        variant="rectangular"
                        width={50}
                        height={22}
                        className="rounded-lg"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "configuracao" && (
          <div className="space-y-2">
            <PillHeaderSkeleton titleWidth={230} withRightContent="badge" />

            {/* Configuração do Agente IA (AgentConfigPanel) */}
            <div className="bg-white border border-purple-200 rounded-3xl p-3 sm:p-4">
              <SectionHeaderSkeleton withAction />
              <div className="space-y-2">
                {/* DNA */}
                <div className="rounded-2xl border border-purple-200 p-3">
                  <div className="mb-2 flex items-center gap-3">
                    <Skeleton
                      variant="rectangular"
                      width={36}
                      height={36}
                      className="rounded-xl"
                    />
                    <Skeleton
                      variant="rectangular"
                      width={130}
                      height={9}
                      className="rounded-sm"
                    />
                  </div>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={90}
                    className="rounded-xl"
                  />
                </div>
                {/* HITL + Comment-to-DM */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      className="rounded-2xl border border-purple-200 p-3 space-y-2"
                    >
                      <div className="flex items-center gap-3">
                        <Skeleton
                          variant="rectangular"
                          width={36}
                          height={36}
                          className="rounded-xl"
                        />
                        <Skeleton
                          variant="rectangular"
                          width={150}
                          height={9}
                          className="rounded-sm"
                        />
                      </div>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={36}
                        className="rounded-xl"
                      />
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={44}
                        className="rounded-xl"
                      />
                    </div>
                  ))}
                </div>
                {/* Quick Replies + Modelo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-purple-200 p-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <Skeleton
                        variant="rectangular"
                        width={36}
                        height={36}
                        className="rounded-xl"
                      />
                      <Skeleton
                        variant="rectangular"
                        width={150}
                        height={9}
                        className="rounded-sm"
                      />
                    </div>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={36}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="rounded-2xl border border-purple-200 p-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <Skeleton
                        variant="rectangular"
                        width={36}
                        height={36}
                        className="rounded-xl"
                      />
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={9}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-xl border-2 border-purple-200 p-2.5 space-y-1.5">
                        <Skeleton
                          variant="rectangular"
                          width="70%"
                          height={10}
                          className="rounded-sm"
                        />
                        <Skeleton
                          variant="rectangular"
                          width="90%"
                          height={8}
                          className="rounded-sm"
                        />
                      </div>
                      <div className="rounded-xl border-2 border-purple-200 p-2.5 space-y-1.5">
                        <Skeleton
                          variant="rectangular"
                          width="70%"
                          height={10}
                          className="rounded-sm"
                        />
                        <Skeleton
                          variant="rectangular"
                          width="90%"
                          height={8}
                          className="rounded-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status por Plataforma */}
            <div className="bg-white border border-purple-200 rounded-3xl p-3 sm:p-4">
              <SectionHeaderSkeleton />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="p-3 rounded-2xl border border-purple-200 flex items-center gap-3"
                  >
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      className="rounded-xl"
                    />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton
                        variant="rectangular"
                        width={90}
                        height={9}
                        className="rounded-sm"
                      />
                      <div className="flex items-center gap-1.5">
                        <Skeleton
                          variant="rectangular"
                          width={95}
                          height={8}
                          className="rounded-sm"
                        />
                        <Skeleton
                          variant="rectangular"
                          width={65}
                          height={14}
                          className="rounded-full"
                        />
                      </div>
                    </div>
                    <Skeleton
                      variant="rectangular"
                      width={80}
                      height={36}
                      className="rounded-lg"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Gerenciador de Tags */}
            <div className="bg-white border border-purple-200 rounded-3xl p-3 sm:p-4">
              <SectionHeaderSkeleton />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={36}
                className="rounded-xl mb-3"
              />
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    width={80}
                    height={16}
                    className="rounded-full"
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoResponderSkeleton;
